/**
 * 为使用了localStorage的地方提供公共方法
 * 提供的方法有：
 *  1.setItem 储存
 *  2.getItem 获取
 *  3.removeItem 删除某个指定键值
 *  4.清除所有该方法类管理的本地储存
 */

import type { AnimType } from '@tencent/slide-sdk-model';
import type { DOCUMENT_TYPE } from '@tencent/ark-model';
import type { STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import jsonSchema from './result.json';

type MsgDataType = {
    slide: any,
    forward: boolean,
    time: number,
};

type Item = {
    localPadId?: string;
    domainId?: string;
    type: 'ADD';
    data: Item;
};

type PerformanceStateType = {
    left: number,
    top: number,
};

type IAllGridInfo = {
    [key: string]: number;
};

type BlankPageDataType = {
    docId: string,
    sourcePadType: DOCUMENT_TYPE,
};

type ThemeType = {
    themeKey?: STSchemeColorVal,
    lumMod: any,
    lumOff: any,
    srgbColor?: string,
};

type RedDotType = {
    show: boolean,
};

type SwitchValueType = {
    [key: string]: boolean
};

enum SYNC_TYPE {
    ALL,
    CURRENT,
    NOT,
}

export enum LocalStorageKeys {
    SLIDE_ADD_PAGE_DROPDOWN = 'slideAddPageDropdown',
    SLIDE_ADD_PAGE_CONTENT = 'slideAddPageContent',
    /** 从列表页点进来 */
    ENTRANCE_FROM_DESKTOPM = 'ENTRANCE_FROM_DESKTOPM',
    SAVED = 'save_performance_stats',
    SAVED_SHOW = 'save_performance_show',
    SPECTACLE_SLIDE= 'spectacle-slide',
    SLIDE_LOCAL_CHART_TIPS_SHOW = 'slide_local_chart_tips_show',
    STORAGE = 'FOLLOW_TIPS',
    TOOLBAR_MODE_SLIDE= 'toolbar_mode_slide',
    EXPTIME_READED_SET = 'exptime-readed-set',
    DOCS_ACTIONS = 'docs_actions',
    ATTACHMENT = 'attachmentPreferenceKey',
    EMBEDFONT = 'embedFontPreferenceKey',
    DISPLAY_GRID_LINE = 'ppt_display_grid_line',
    USE_GRID_ALIGN = 'ppt_use_grid_align',
    USE_GRID_INFO= 'ppt_doc_grid_info',
    DISPLAY_GUIDES = 'ppt_display_guides',
    ENABLE_SNAP_LINE_STATE = 'ppt_enable_snap_line_state',
    IS_SYNC_LIST = 'IS_SYNC_TO_B_LIST',
    DISPLAY_TEXT_RULER = 'ppt_display_text_ruler',
    LOCAL_SLIDE_CUSTOM_THEME = 'local_slide_custom_theme_key',
    LOCAL_SLIDE_CUSTOM_THEME_INDEX = 'local_slide_custom_theme_index',
    /** 新版工具栏是否已经在该客户端展示过 */
    HAS_NEW_TOOLBAR_SHOWED = 'has_new_toolbar_showed',
    BLANK_PAGE_DATA = 'blankPageData',
    I18NLAN = 'i18nLan',
    FORECOLOR = 'foreColor_recent_color',
    BACKCOLOR = 'backColor_recent_color',
    BORDER_COLOR_RECENT_COLOR = 'border-color_recent_color',
    FILLCOLOR = 'fill-color_recent_color',
    FORECOLOR_THEME = 'foreColor_recent_theme',
    BACKCOLOR_THEME = 'backColor_recent_theme',
    BORDERCOLOR_THEME = 'border-color_recent_theme',
    FILLCOLOR_THEME = 'fill-color_recent_theme',
    RECENT_ANIMATION_STORAGE = 'slide_recent_animation',
    VERSION_HIT = 'PERFORMANCE_LAST_CODE_VERSION',
    SVIP_TITLE_COLOR_RED_DOT = 'UserStorage::SvipTitleColorRedDot',
    SHIPLY_SWITCH_VALUE = 'UserStorage::ShiplySwitchValue',
    TRANSITION_AUTO_PREVIEW = 'UserStorage::TransitionAutoPreview',
}

// LocalStorageValues：规约储存的key对应的value类型
interface LocalStorageValues {
    // 普通业务
    [LocalStorageKeys.SLIDE_ADD_PAGE_DROPDOWN]: boolean,
    [LocalStorageKeys.SLIDE_ADD_PAGE_CONTENT]: boolean,
    [LocalStorageKeys.ENTRANCE_FROM_DESKTOPM]: boolean,
    [LocalStorageKeys.SAVED]: PerformanceStateType,
    [LocalStorageKeys.SAVED_SHOW]: boolean,
    [LocalStorageKeys.SPECTACLE_SLIDE]: MsgDataType,
    [LocalStorageKeys.SLIDE_LOCAL_CHART_TIPS_SHOW]: string,
    [LocalStorageKeys.STORAGE]: number,
    [LocalStorageKeys.TOOLBAR_MODE_SLIDE]: string,
    [LocalStorageKeys.EXPTIME_READED_SET]: string,
    [LocalStorageKeys.DOCS_ACTIONS]: Item[],
    [LocalStorageKeys.ATTACHMENT]: boolean,
    [LocalStorageKeys.EMBEDFONT]: boolean,
    [LocalStorageKeys.DISPLAY_GRID_LINE]: number,
    [LocalStorageKeys.USE_GRID_ALIGN]: number,
    [LocalStorageKeys.USE_GRID_INFO]: IAllGridInfo,
    [LocalStorageKeys.DISPLAY_GUIDES]: number,
    [LocalStorageKeys.VERSION_HIT]: string,
    [LocalStorageKeys.ENABLE_SNAP_LINE_STATE]: string,
    [LocalStorageKeys.DISPLAY_TEXT_RULER]: string,
    [LocalStorageKeys.LOCAL_SLIDE_CUSTOM_THEME_INDEX]: number,
    [LocalStorageKeys.HAS_NEW_TOOLBAR_SHOWED]: boolean,
    [LocalStorageKeys.BLANK_PAGE_DATA]: BlankPageDataType,
    [LocalStorageKeys.BORDER_COLOR_RECENT_COLOR]: string,
    [LocalStorageKeys.I18NLAN]: string,
    [LocalStorageKeys.FORECOLOR]: string,
    [LocalStorageKeys.BACKCOLOR]: string,
    [LocalStorageKeys.FILLCOLOR]: string,
    [LocalStorageKeys.FORECOLOR_THEME]: ThemeType,
    [LocalStorageKeys.BACKCOLOR_THEME]: ThemeType,
    [LocalStorageKeys.BORDERCOLOR_THEME]: ThemeType,
    [LocalStorageKeys.FILLCOLOR_THEME]: ThemeType,
    [LocalStorageKeys.RECENT_ANIMATION_STORAGE]: AnimType[],
    [LocalStorageKeys.SVIP_TITLE_COLOR_RED_DOT]: RedDotType,
    [LocalStorageKeys.SHIPLY_SWITCH_VALUE]: SwitchValueType,
    [LocalStorageKeys.TRANSITION_AUTO_PREVIEW]: number,
    [LocalStorageKeys.IS_SYNC_LIST]: SYNC_TYPE,
    [LocalStorageKeys.LOCAL_SLIDE_CUSTOM_THEME]: any[],
}

class UserLocalStorage {
    // UserLocalStorage的实例
    private static instance: UserLocalStorage;

    public static getInstance() {
        // 判断实例是否存在，保证只创建一个实例
        if (!UserLocalStorage.instance) {
            UserLocalStorage.instance = new UserLocalStorage();
        }
        return UserLocalStorage.instance;
    }

    /**
     * @returns true——储存成功  false——value与预期存储类型不符合，储存失败
     */
    public setItem<T extends LocalStorageKeys>(
        key: T,
        value: LocalStorageValues[T],
    ): boolean {
        // 获取预期类型
        const expectValueType = jsonSchema.properties[key as LocalStorageKeys]?.type;
        // 获取当前类型
        const valueType = this.getType(value);
        // 若获取到的value与预期值不符
        if (expectValueType !== valueType) return false;

        // 判断是否为字符串
        if (valueType === 'string') {
            localStorage.setItem(key, value as string);
            return true;
        }

        localStorage.setItem(key, JSON.stringify(value));
        return true;
    }

    public getItem<T extends LocalStorageKeys>(key: T): LocalStorageValues[T] | null;
    public getItem<T extends LocalStorageKeys>(
        key: T,
        defaultValue:  Exclude<LocalStorageValues[T], null>,
    ): Exclude<LocalStorageValues[T], null>;
    public getItem<T extends LocalStorageKeys>(
        key: T,
        defaultValue?: Exclude<LocalStorageValues[T], null>,
    ): LocalStorageValues[T] | null | Exclude<LocalStorageValues[T], null> {
        const value = localStorage.getItem(key);

        // 若值为null，返回默认值；未传默认值则返回null
        if (value === null) return defaultValue ?? null;

        // 获取key对应value的预期类型
        const expectValueType = jsonSchema.properties[key as LocalStorageKeys]?.type;

        // 若预期为字符串，则直接返回
        if (expectValueType === 'string') {
            return value as LocalStorageValues[T];
        }

        // 获取取出来值的类型
        const parseValue: LocalStorageValues[T] | null = this.parseJson(value, defaultValue);
        const parseValueType = this.getType(parseValue);

        // 若取出来的值与预期不符，则返回默认值；若未传默认值，则返回null
        if (parseValueType !== expectValueType) return defaultValue ?? null;

        return parseValue;
    }

    /**
     * 存储/修改 对象的方法
     * 使用方法类似于setState
     * @param objKey 键
     * @param objItem 要添加或更改的对象
     * @returns true——储存成功  false——value与预期存储类型不符合，储存失败
     */
    public setObjItem<T extends LocalStorageKeys>(
        objKey: T,
        objItem: Object,
    ): boolean {
        let obj = this.getItem(objKey);
        // 获取预期类型
        const expectType = jsonSchema.properties[objKey as LocalStorageKeys]?.type;
        // 获取当前类型
        const currentType = this.getType(obj);

        // 若预期类型不是对象或当前类型不是对象，则返回false，储存失败
        if (expectType !== 'object' || currentType !== 'object') return false;

        // 若当前值是否为null，则将新对象直接赋值
        if (objItem === null) {
            obj = objItem as LocalStorageValues[T];
        } else {
            // 当前值不是null，则将新对象与取出来的对象进行合并
            (obj as Object) = {
                ...obj as Object,
                ...objItem,
            };
        }
        localStorage.setItem(objKey, JSON.stringify(obj));
        return true;
    }

    /**
     * 移除某个键对应的值
     * @param key 键
     * @returns true-删除成功 false-无对应key
     */
    public removeItem(key: LocalStorageKeys) {
        const value = this.getItem(key);
        if (value === null) return false;

        localStorage.removeItem(key);
        return true;
    }

    /**
     *  清除该方法管理的所有本地储存
     */
    public clearAllItems() {
        Object.keys(LocalStorageKeys).forEach((localStorageKey) => {
            localStorage.removeItem(localStorageKey);
        });
    }

    /**
     * 处理解析JSON字符串时出现的异常
     */
    private parseJson(jsonStr: string, defaultValue: any): any {
        try {
            return JSON.parse(jsonStr);
        } catch {
            return defaultValue || null;
        }
    }

    /**
     * 检测value的具体数据类型
     * @param value 被检测的变量
     */
    private getType(value: any): string {
        const typeString = Object.prototype.toString.call(value);
        return typeString.split(' ')[1].slice(0, -1).toLocaleLowerCase();
    }
}

const userLocalStorage = UserLocalStorage.getInstance();
export default userLocalStorage;
